import { trackBusinessError } from '@sxm-de/dev-kits/web-sdk/utils';
import { daysIntoDateRange } from '@sxm-core/utility-code/typescript/browser-common-date-time';
import { DomainInitPayload } from '@sxm-de/dev-kits/web-sdk/types';

interface RedeemMagicLinkForDeviceClaimTrialFnPayload {
    magicLinkId: string;
}

type Status =
    | 'TrialNotStarted'
    | 'TrialNotClaimed'
    | 'TrialClaimed'
    | 'TrialWithFollowOn'
    | 'SelfPay';

interface DeviceStatus {
    status: Status;
    oemId: string;
    radioId: string;
    vehicle: { year: number; make: string; model: string } | null;
    daysBeforeTrialEndDate: number;
}

export type RedeemMagicLinkForDeviceClaimTrialFn = (
    payload: RedeemMagicLinkForDeviceClaimTrialFnPayload,
) => Promise<DeviceStatus>;

export function initRedeemMagicLinkForDeviceClaimTrialFn({
    dependencies: { appEventSdk, clientSdk, atlasSdk },
}: DomainInitPayload): RedeemMagicLinkForDeviceClaimTrialFn {
    return async ({
        magicLinkId,
    }: RedeemMagicLinkForDeviceClaimTrialFnPayload) => {
        try {
            const { grant } = await clientSdk.user.magicLinkRedeem(magicLinkId);
            const state =
                await atlasSdk.domains.user.deviceStatusByMagicLinkGrant({
                    request: { grant },
                });

            let status: Status = 'TrialNotStarted';
            let daysBeforeTrialEndDate = 0;
            if (state.lifecycleState.trialEligible) {
                status = 'TrialNotStarted';
            } else if (state.lifecycleState.inactive) {
                status = 'TrialClaimed';
            } else if (state.lifecycleState.trial?.phase) {
                const { totalNumberOfDays, daysIn } = daysIntoDateRange(
                    state.lifecycleState.trial.startsAt,
                    state.lifecycleState.trial.expiresAt,
                    new Date(Date.now()).toUTCString(),
                );
                daysBeforeTrialEndDate = totalNumberOfDays - daysIn;
                if (state.lifecycleState.trial.phase.anonymous) {
                    status = 'TrialNotClaimed';
                } else if (state.lifecycleState.trial.phase.claimed) {
                    status = 'TrialClaimed';
                } else if (state.lifecycleState.trial.phase.followOn) {
                    status = 'TrialWithFollowOn';
                }
            } else if (state.lifecycleState.selfPay) {
                status = 'SelfPay';
            }

            appEventSdk.trackEvent({
                event: 'device-info',
                deviceInfo: {
                    vin: state.vehicleState?.vin,
                    vid: state.radioId,
                    year: state.vehicleState?.year,
                    make: state.vehicleState?.make,
                    model: state.vehicleState?.model,
                },
            });
            return {
                status,
                oemId: state.oemDriverId,
                radioId: state.radioId,
                vehicle: state.vehicleState,
                daysBeforeTrialEndDate,
            };
        } catch (error: any) {
            switch (error.code) {
                case 'userServices.magicLink.magicLinkNotFound':
                    trackBusinessError(appEventSdk, error);
                    throw new Error('MAGIC_LINK_NOT_FOUND');
                case 'userServices.magicLink.magicLinkExpired':
                    trackBusinessError(appEventSdk, error);
                    throw new Error('MAGIC_LINK_EXPIRED');
                case 'userServices.magicLink.invalidBaseUrl':
                    trackBusinessError(appEventSdk, error);
                    throw new Error('INVALID_BASE_URL');
                default:
                    /**
                     * TODO:
                     * Log error to error logger (not analytics).
                     */
                    throw new Error('SYSTEM');
            }
        }
    };
}
